/**
 * A centred, looping row of videos. The active video sits in the middle at full size; the others
 * shrink and fade on both sides. Slides are positioned with transforms from their offset to the
 * active one (wrapped so the row loops), so no clones are needed.
 *
 * Markup contract: `[data-slide]` slides inside the element, optional `[data-previous]`,
 * `[data-next]`, `[data-dot]` buttons and a `[data-status]` live region. `data-status-template`
 * holds the translated "Slide [index] of [length]" text.
 */
class VideoSpotlight extends HTMLElement {
  /** @type {HTMLElement[]} */
  #slides = [];

  /** @type {HTMLElement | null} */
  #stage = null;

  #active = 0;

  /** @type {AbortController | null} */
  #abort = null;

  /** @type {ResizeObserver | null} */
  #resize = null;

  #dragStartX = 0;

  #dragging = false;

  #moved = false;

  /** Scale and dimming (how much plum is laid over the video) by distance from the centre */
  static scales = [1, 0.76, 0.56];

  static dims = [0, 0.4, 0.65];

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#stage = this.querySelector('.video-spotlight__stage');
    this.#slides = [...this.querySelectorAll('[data-slide]')].filter((slide) => slide instanceof HTMLElement);
    if (!this.#stage || this.#slides.length === 0) return;

    this.querySelectorAll('[data-dot]').forEach((dot) => {
      dot.addEventListener('click', () => this.go(Number(dot.getAttribute('data-dot'))), { signal });
    });

    this.#stage.addEventListener(
      'keydown',
      (event) => {
        const rtl = getComputedStyle(this).direction === 'rtl';
        if (event.key === 'ArrowLeft') this.go(this.#active + (rtl ? 1 : -1));
        else if (event.key === 'ArrowRight') this.go(this.#active + (rtl ? -1 : 1));
        else return;
        event.preventDefault();
      },
      { signal }
    );

    // Swipe or drag, and a click on a side video brings it to the centre
    this.#stage.addEventListener('pointerdown', (event) => this.#onPointerDown(event), { signal });
    this.#stage.addEventListener('pointermove', (event) => this.#onPointerMove(event), { signal });
    this.#stage.addEventListener('pointerup', (event) => this.#onPointerUp(event), { signal });
    this.#stage.addEventListener('pointercancel', () => (this.#dragging = false), { signal });
    this.#stage.addEventListener(
      'click',
      (event) => {
        if (this.#moved) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        const target = event.target instanceof Element ? event.target.closest('[data-slide]') : null;
        const index = target ? this.#slides.indexOf(/** @type {HTMLElement} */ (target)) : -1;
        if (index >= 0 && index !== this.#active) this.go(index);
      },
      { signal, capture: true }
    );

    // The centre video plays while the mouse is over it (touch screens tap to play instead)
    this.#stage.addEventListener(
      'pointerover',
      (event) => {
        if (event.pointerType !== 'mouse') return;
        const media = event.target instanceof Element ? event.target.closest('.video-spotlight__media') : null;
        if (media && !media.contains(/** @type {Node | null} */ (event.relatedTarget))) this.#hover(media, true);
      },
      { signal }
    );
    this.#stage.addEventListener(
      'pointerout',
      (event) => {
        if (event.pointerType !== 'mouse') return;
        const media = event.target instanceof Element ? event.target.closest('.video-spotlight__media') : null;
        if (media && !media.contains(/** @type {Node | null} */ (event.relatedTarget))) this.#hover(media, false);
      },
      { signal }
    );

    this.#resize = new ResizeObserver(() => this.#render(false));
    this.#resize.observe(this.#stage);
    this.#render(false);
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#resize?.disconnect();
  }

  /**
   * Moves the row so a slide is in the centre. The index wraps round, so going past the last
   * video lands on the first.
   * @param {number} index
   */
  go(index) {
    const count = this.#slides.length;
    const next = ((index % count) + count) % count;
    if (next === this.#active) return;

    // Moving on stops the video that was playing
    const playing = this.#slides[this.#active]?.querySelector('deferred-media');
    if (playing && 'pauseMedia' in playing && typeof playing.pauseMedia === 'function') playing.pauseMedia();

    this.#active = next;
    this.#render(true);
  }

  /**
   * Plays the centre video while the mouse is over it and pauses it when the mouse leaves.
   * @param {Element} media - The picture area of a slide
   * @param {boolean} over - True when the mouse enters, false when it leaves
   */
  #hover(media, over) {
    const slide = media.closest('[data-slide]');
    if (!(slide instanceof HTMLElement) || slide.dataset.offset !== '0') return;
    const deferred = slide.querySelector('deferred-media');
    if (!deferred) return;

    if (!over) {
      if ('pauseMedia' in deferred && typeof deferred.pauseMedia === 'function') deferred.pauseMedia();
      return;
    }

    if (!deferred.getAttribute('data-media-loaded') && 'loadContent' in deferred && typeof deferred.loadContent === 'function') {
      // Load the video without moving keyboard focus onto it
      deferred.loadContent(false);
    } else if ('playMedia' in deferred && typeof deferred.playMedia === 'function') {
      deferred.playMedia();
    }

    // A silent loop without the browser's controls, like a reel
    const video = [...deferred.querySelectorAll('video')].pop();
    if (video instanceof HTMLVideoElement) {
      video.controls = false;
      video.muted = true;
      video.loop = true;
    }
  }

  /** @param {boolean} announce */
  #render(announce) {
    if (!this.#stage) return;
    const count = this.#slides.length;
    // Layout width, which a transform does not change
    const width = this.#slides[0]?.offsetWidth || 0;
    const gap = 16;
    const scales = VideoSpotlight.scales;
    const dims = VideoSpotlight.dims;

    // Distance of each step from the centre, from the slide width, scale and gap
    const x = [0];
    for (let step = 1; step < scales.length; step++) {
      const previous = scales[step - 1] ?? 1;
      const current = scales[step] ?? 0.5;
      x.push((x[step - 1] ?? 0) + (width * previous) / 2 + gap + (width * current) / 2);
    }

    const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;

    this.#slides.forEach((slide, i) => {
      let offset = i - this.#active;
      if (offset > count / 2) offset -= count;
      if (offset < -count / 2) offset += count;
      const distance = Math.abs(offset);
      const sign = offset < 0 ? -1 : 1;
      const visible = distance < scales.length;
      const position = visible ? (x[distance] ?? 0) * sign * rtl : (x[scales.length - 1] ?? 0) * sign * rtl;

      slide.dataset.offset = String(offset);
      slide.style.transform = `translateX(calc(-50% + ${position}px))`;
      slide.style.setProperty('--spotlight-scale', String(visible ? (scales[distance] ?? 0.5) : 0.5));
      slide.style.setProperty('--spotlight-dim', String(visible ? (dims[distance] ?? 0.65) : 0.65));
      slide.style.opacity = visible ? '1' : '0';
      slide.style.zIndex = String(10 - distance);
      slide.setAttribute('aria-hidden', distance === 0 ? 'false' : 'true');
      // Only the centre video can be reached with the keyboard or played
      slide.querySelectorAll('button, a, video').forEach((control) => {
        if (distance === 0) control.removeAttribute('tabindex');
        else control.setAttribute('tabindex', '-1');
      });
      slide.style.pointerEvents = visible ? '' : 'none';
    });

    this.querySelectorAll('[data-dot]').forEach((dot) => {
      dot.setAttribute('aria-current', String(Number(dot.getAttribute('data-dot')) === this.#active));
    });

    if (announce) {
      const template = this.dataset.statusTemplate || '';
      const status = this.querySelector('[data-status]');
      if (status && template) {
        status.textContent = template.replace('[index]', String(this.#active + 1)).replace('[length]', String(count));
      }
    }
  }

  /** @param {PointerEvent} event */
  #onPointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.#dragging = true;
    this.#moved = false;
    this.#dragStartX = event.clientX;
  }

  /** @param {PointerEvent} event */
  #onPointerMove(event) {
    if (!this.#dragging) return;
    if (Math.abs(event.clientX - this.#dragStartX) > 8) this.#moved = true;
  }

  /** @param {PointerEvent} event */
  #onPointerUp(event) {
    if (!this.#dragging) return;
    this.#dragging = false;
    const delta = event.clientX - this.#dragStartX;
    const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
    if (Math.abs(delta) > 40) this.go(this.#active + (delta < 0 ? 1 : -1) * rtl);
    // Let the click that follows a drag be ignored, then reset
    setTimeout(() => (this.#moved = false), 0);
  }
}

if (!customElements.get('video-spotlight')) {
  customElements.define('video-spotlight', VideoSpotlight);
}
